import { Client, fetchExchange, mapExchange, subscriptionExchange } from 'urql'
import { cacheExchange } from '@urql/exchange-graphcache'
import { getSourcesQuery, getUsersQuery, useAuthStore } from '@wove/api'
import type { Cache } from '@urql/exchange-graphcache'
import type {GetSourcesQuery, UsersQuery} from '@wove/api/types'
import rollbar from '@src/util/rollbar'
import { createClient as createWSClient } from 'graphql-ws'

const clientCache = cacheExchange({
  updates: {
    Mutation: {
      deleteSource: (_result, args: { id: string }, cache: Cache) => {
        cache.updateQuery<GetSourcesQuery>({ query: getSourcesQuery }, (data) => {
          if (!data?.getSources) {
            return data
          }
          const updatedSources = data.getSources.filter((source) => source.id !== args.id)
          return { ...data, getSources: updatedSources }
        })
      },
      inviteUsers: (_result, _args, cache: Cache) => {
        cache.invalidate({
          __typename: 'Query',
          fieldName: 'getUsers',
        })
      },
      deactivateUser: (_result, args: { id: string }, cache: Cache) => {
        cache.updateQuery<UsersQuery>({ query: getUsersQuery }, (data) => {
          if (!data?.getUsers?.users) {
            return data
          }
          data.getUsers.users = data.getUsers.users.map((user) => {
            if (user.id === args.id) {
              return { ...user, deactivatedAt: new Date().toISOString() }
            }
            return user
          })
          return data
        })
      },
    },
  },
  keys: {
    CurrentSessionResponse: () => null,
    UsersResponse: () => null,
    Price: () => null,
    RateLineItem: () => null,
    ShippingRate: () => null,
    Location: () => null,
    ContainerRate: () => null,
    Carrier: () => null,
    SpreadsheetLocation: () => null,
  },
})

export function createClient(initialToken?: string, initialApiUrl?: string) {
  // Create URL getter that uses current auth store value
  const getUrl = () => {
    const authStore = useAuthStore.getState()
    const currentApiUrl = authStore.apiUrl || initialApiUrl
    return currentApiUrl ? `${currentApiUrl}/graphql` : 'http://localhost:4000/graphql'
  }
  
  // Create WebSocket URL
  const getWsUrl = () => {
    const url = getUrl()
    return url.replace(/^http/, 'ws').replace('/graphql', '/subscriptions')
  }
  
  // Create WebSocket client for subscriptions with auto-reconnect
  const wsClient = createWSClient({
    url: getWsUrl(),
    connectionParams: () => {
      const authStore = useAuthStore.getState()
      const currentToken = authStore.accessToken || initialToken
      return {
        authorization: currentToken ? `Bearer ${currentToken}` : undefined,
      }
    },
    shouldRetry: () => true, // Always retry
    retryAttempts: Infinity, // Keep retrying indefinitely
    retryWait: (retries) => {
      // Exponential backoff: 1s, 2s, 4s, 8s, 16s, then cap at 30s
      const delay = Math.min(1000 * Math.pow(2, retries), 30000)
      console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${retries + 1})`)
      return new Promise(resolve => setTimeout(resolve, delay))
    },
    keepAlive: 10_000, // Send ping every 10 seconds to keep connection alive
    on: {
      connected: () => {
        console.log('[WebSocket] Connected to GraphQL subscriptions')
      },
      error: (error) => {
        console.error('[WebSocket] Connection error:', error)
      },
      closed: (event) => {
        console.log('[WebSocket] Connection closed:', event)
      },
      ping: (received) => {
        if (!received) {
          console.log('[WebSocket] Ping sent')
        }
      },
      pong: (received) => {
        if (received) {
          console.log('[WebSocket] Pong received')
        }
      },
    },
    lazy: false, // Connect immediately
  })
  
  // Add network connectivity monitoring
  if (typeof window !== 'undefined') {
    // Handle online/offline events
    window.addEventListener('online', () => {
      console.log('[WebSocket] Network online - triggering reconnect')
      // The graphql-ws client will automatically reconnect due to our retry configuration
    })
    
    window.addEventListener('offline', () => {
      console.log('[WebSocket] Network offline')
    })
    
    // Handle page visibility changes (reconnect when tab becomes visible)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        console.log('[WebSocket] Page visible - checking connection')
        // The keepAlive mechanism will detect if connection is lost and reconnect
      }
    })
  }
  
  return new Client({
    url: getUrl(),
    fetchOptions: () => {
      // Get the current token from the auth store at request time
      const authStore = useAuthStore.getState()
      const currentToken = authStore.accessToken || initialToken
      
      const headers: Record<string, string> = {
        'apollo-require-preflight': 'true',
      }
      if (currentToken) {
        headers['Authorization'] = `Bearer ${currentToken}`
      }
      return { headers }
    },
    exchanges: [
      mapExchange({
        onError(error) {
          rollbar.error(error)
        },
      }),
      clientCache,
      fetchExchange,
      subscriptionExchange({
        forwardSubscription: (operation) => ({
          subscribe: (sink) => ({
            unsubscribe: wsClient.subscribe(operation as any, sink),
          }),
        }),
      }),
    ],
  })
}
